/**
 * PROMPT 1: ADD WATCH WIZARD - 3 Step, Zero Jargon
 * 
 * Paste this into v0.dev or Cursor:
 * You are a senior React + Tailwind + shadcn developer.
 * 
 * TASK: Build the "Add Watch Wizard" for UptimeWing. Goal: Non-technical user adds first monitor in <60 seconds.
 * 
 * TECH: Next.js 14, React, TypeScript, Tailwind, shadcn/ui, framer-motion, zod
 * 
 * REQUIREMENTS:
 * 1. UI STRUCTURE
 *     Component: `<AddWatchWizard />`
 *     Modal with 3 steps and a progress bar at top: "Step 1 of 3"
 *     Buttons: [Back] [Next] [Create My Watch]
 *     On final step, show confetti + "Your watch is live" using framer-motion
 * 
 * 2. STEP 1: WHAT DO YOU WANT TO WATCH?
 *     Title: "What do you want to watch?"
 *     4 big clickable cards with icons:
 *     [🌐 Website] "Check if a website loads"
 *     [⚡ API] "Check if an API returns data"
 *     [🖥️ Server] "Check if a server port is open"
 *     [🎮 Game Server] "Check Minecraft, CS2, etc"
 *     On select, highlight card with primary color border
 * 
 * 3. STEP 2: TELL US THE ADDRESS
 *     Title: "Where is it?"
 *     Dynamic input based on Step 1:
 *     If Website/API: `<Input placeholder="https://myshop.com" />`
 *     If Server: 2 inputs: `[IP Address] [Port]`
 *     OnBlur: Show smart detection chip with framer-motion fade in:
 *     "Found: Website. I'll check if it loads every 1 minute and alert you if it's down for 2 minutes."
 *     [✓ Sounds Good] [Advanced Settings]
 *     If "Advanced" clicked, expand accordion:
 *     Check Every: [1 min] [5 min] [10 min] dropdown
 *     Timeout: [10s] input
 * 
 * 4. STEP 3: WHO SHOULD WE TELL?
 *     Title: "How should we reach you?"
 *     Show: "We'll email user@email.com if there's a problem" with mail icon
 *     Section: "Also notify:"
 *     Buttons: [+ Telegram] [+ Discord] [+ Slack] - these are just UI for now, no logic needed
 *     Final CTA: "Create My Watch" - Primary button
 * 
 * 5. RULES
 *     Use shadcn: Card, Button, Input, Progress, Accordion, Badge
 *     Zero jargon. Use "Watch" not "Monitor". "Address" not "Endpoint".
 *     Validate with zod. Step 2 input must not be empty.
 *     Mobile responsive.
 * 
 * OUTPUT: Single TSX file with all components and mock onSubmit function that console.logs the data.
 */

import React, { useState } from "react";
import { X, Globe, Zap, Server, Gamepad2, Sparkles, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { TYPE_METADATA } from "@/data/jargon-dictionary";

interface AddWatchWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (watch: {
    name: string;
    url: string;
    monitor_type: string;
    check_interval_minutes: number;
    grace_period_minutes: number;
  }) => void;
}

export function AddWatchWizard({ open, onClose, onCreated }: AddWatchWizardProps) {
  const [step, setStep] = useState(1);
  const [watchType, setWatchType] = useState<string>("website");
  const [address, setAddress] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [port, setPort] = useState("");
  const [customInterval, setCustomInterval] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiConfirmed, setAiConfirmed] = useState(false);

  if (!open) return null;

  const handleAddressBlur = () => {
    const finalVal = watchType === "server" ? ipAddress : address;
    if (!finalVal.trim()) return;
    
    setIsAiProcessing(true);
    setTimeout(() => {
      setIsAiProcessing(false);
      setAiConfirmed(true);

      // Rule based AI detection
      if (watchType !== "server") {
        if (address.includes("api") || address.includes("v1") || address.includes("graphql")) {
          setWatchType("api");
        } else if (address.includes("mc") || address.includes("play") || address.includes("roblox")) {
          setWatchType("game");
        } else {
          setWatchType("website");
        }
      }
    }, 850);
  };

  const handleConfirmStep2 = () => {
    setStep(3);
  };

  const handleSubmit = () => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.55 }
    });

    const metadata = TYPE_METADATA[watchType as keyof typeof TYPE_METADATA] || TYPE_METADATA.website;
    const finalUrl = watchType === "server" ? `${ipAddress}:${port || "80"}` : address;
    let cleanName = finalUrl
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0];

    onCreated({
      name: cleanName || "My Watch Check",
      url: watchType === "server" ? finalUrl : (finalUrl.startsWith("http") ? finalUrl : `https://${finalUrl}`),
      monitor_type: watchType,
      check_interval_minutes: customInterval,
      grace_period_minutes: metadata.defaultGrace,
    });
    
    toast("Your watch is live. We'll check it in 30 seconds.");
    onClose();
    // reset state
    setStep(1);
    setAddress("");
    setIpAddress("");
    setPort("");
    setAiConfirmed(false);
    setShowAdvanced(false);
  };

  const selectedMeta = TYPE_METADATA[watchType as keyof typeof TYPE_METADATA] || TYPE_METADATA.website;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-background text-foreground border-4 border-foreground w-full max-w-xl p-6 rounded-none shadow-[8px_8px_0_0_#000] dark:shadow-[8px_8px_0_0_#fff] relative space-y-6 font-sans">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 hover:bg-muted border-2 border-transparent hover:border-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Progress Tracker bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-mono font-black uppercase tracking-wider text-muted-foreground">
            <span>Step {step} of 3</span>
            <span>{step === 1 ? "Choose Target" : step === 2 ? "Set Address" : "Alert Setup"}</span>
          </div>
          <Progress value={(step / 3) * 100} className="h-2.5 rounded-none bg-muted border border-foreground" />
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: WHAT DO YOU WANT TO WATCH? */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-4"
            >
              <h2 className="text-xl font-black uppercase tracking-tight">What do you want to watch?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Card
                  onClick={() => setWatchType("website")}
                  className={`cursor-pointer rounded-none border-2 transition-all p-3 hover:bg-muted ${
                    watchType === "website" ? "border-primary bg-primary/5" : "border-foreground/30 bg-card"
                  }`}
                >
                  <CardContent className="p-1.5 space-y-2">
                    <div className="text-2xl">🌐</div>
                    <div className="font-black uppercase text-xs">Website</div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Check if a website loads</p>
                  </CardContent>
                </Card>

                <Card
                  onClick={() => setWatchType("api")}
                  className={`cursor-pointer rounded-none border-2 transition-all p-3 hover:bg-muted ${
                    watchType === "api" ? "border-primary bg-primary/5" : "border-foreground/30 bg-card"
                  }`}
                >
                  <CardContent className="p-1.5 space-y-2">
                    <div className="text-2xl">⚡</div>
                    <div className="font-black uppercase text-xs">API</div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Check if an API returns data</p>
                  </CardContent>
                </Card>

                <Card
                  onClick={() => setWatchType("server")}
                  className={`cursor-pointer rounded-none border-2 transition-all p-3 hover:bg-muted ${
                    watchType === "server" ? "border-primary bg-primary/5" : "border-foreground/30 bg-card"
                  }`}
                >
                  <CardContent className="p-1.5 space-y-2">
                    <div className="text-2xl">🖥️</div>
                    <div className="font-black uppercase text-xs">Server</div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Check if a server port is open</p>
                  </CardContent>
                </Card>

                <Card
                  onClick={() => setWatchType("game")}
                  className={`cursor-pointer rounded-none border-2 transition-all p-3 hover:bg-muted ${
                    watchType === "game" ? "border-primary bg-primary/5" : "border-foreground/30 bg-card"
                  }`}
                >
                  <CardContent className="p-1.5 space-y-2">
                    <div className="text-2xl">🎮</div>
                    <div className="font-black uppercase text-xs">Game Server</div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Check Minecraft, CS2, etc</p>
                  </CardContent>
                </Card>
              </div>

              <div className="text-[10px] font-mono font-bold text-muted-foreground uppercase text-center py-2">
                Not sure? Pick Website
              </div>

              <Button
                onClick={() => setStep(2)}
                className="w-full rounded-none border-2 border-foreground h-11 bg-primary text-primary-foreground font-black uppercase text-xs shadow-xs"
              >
                Next Step
              </Button>
            </motion.div>
          )}

          {/* STEP 2: TELL US THE ADDRESS */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-4"
            >
              <h2 className="text-xl font-black uppercase tracking-tight">Where is it?</h2>

              {watchType === "server" ? (
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <label className="text-[9px] font-mono font-black uppercase text-muted-foreground">IP Address</label>
                    <Input
                      placeholder="e.g. 192.168.1.1"
                      value={ipAddress}
                      onChange={(e) => setIpAddress(e.target.value)}
                      onBlur={handleAddressBlur}
                      className="h-11 rounded-none border-2 border-foreground font-mono text-xs bg-background"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono font-black uppercase text-muted-foreground">Port</label>
                    <Input
                      placeholder="e.g. 25565"
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                      onBlur={handleAddressBlur}
                      className="h-11 rounded-none border-2 border-foreground font-mono text-xs bg-background"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label className="text-[9px] font-mono font-black uppercase text-muted-foreground">Address</label>
                  <Input
                    placeholder="https://myshop.com"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onBlur={handleAddressBlur}
                    className="h-11 rounded-none border-2 border-foreground font-mono text-xs bg-background"
                  />
                </div>
              )}

              {isAiProcessing && (
                <div className="flex items-center gap-2 text-xs font-mono font-black text-muted-foreground py-1">
                  <Sparkles className="h-4 w-4 animate-spin text-yellow-500" />
                  <span>AI Scanning link configuration...</span>
                </div>
              )}

              {aiConfirmed && !isAiProcessing && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border-2 border-foreground p-4 bg-muted space-y-3"
                >
                  <div className="text-xs font-mono font-black text-foreground leading-relaxed flex gap-2">
                    <Sparkles className="h-4 w-4 shrink-0 text-yellow-500 mt-0.5 animate-pulse" />
                    <span>
                      Found: <strong>{selectedMeta.label}</strong>. We'll check if it loads every{" "}
                      <strong>{customInterval} minute{customInterval > 1 ? "s" : ""}</strong> and alert you if it's down.
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleConfirmStep2}
                      className="flex-1 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase text-[10px] h-8"
                    >
                      ✓ Sounds Good
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="rounded-none border-2 border-foreground bg-background font-black uppercase text-[10px] h-8"
                    >
                      Advanced Settings
                    </Button>
                  </div>

                  {showAdvanced && (
                    <div className="border-t border-foreground/20 pt-3 mt-1 space-y-3 font-mono text-[10px]">
                      <div className="space-y-1">
                        <label className="font-black uppercase text-muted-foreground flex justify-between">
                          <span>Check Every:</span>
                          <select
                            value={customInterval}
                            onChange={(e) => setCustomInterval(Number(e.target.value))}
                            className="bg-background border border-foreground text-[10px] font-bold p-1 rounded-none outline-none"
                          >
                            <option value="1">1 minute</option>
                            <option value="5">5 minutes</option>
                            <option value="10">10 minutes</option>
                          </select>
                        </label>
                        <p className="text-[9px] text-muted-foreground mt-1">
                          Checking more often uses more resources. 1 minute is perfect for most websites.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <label className="font-black uppercase text-muted-foreground flex justify-between items-center">
                          <span>Timeout limit:</span>
                          <Input
                            type="text"
                            placeholder="10s"
                            className="h-6 w-16 text-center text-[10px] border border-foreground bg-background rounded-none py-0.5"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 rounded-none border-2 border-foreground font-black uppercase text-xs h-11"
                >
                  Back
                </Button>
                <Button
                  onClick={handleConfirmStep2}
                  disabled={watchType === "server" ? !ipAddress.trim() : !address.trim()}
                  className="flex-1 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase text-xs h-11"
                >
                  Next Step
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: WHO SHOULD WE TELL? */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-4"
            >
              <h2 className="text-xl font-black uppercase tracking-tight">How should we reach you?</h2>
              
              <div className="flex items-center gap-3 border-2 border-foreground p-3 bg-muted">
                <Mail className="h-5 w-5 text-primary shrink-0" />
                <span className="text-[10px] font-mono font-black uppercase">
                  We'll email user@email.com if there's a problem
                </span>
              </div>

              <div className="space-y-2">
                <div className="text-[9px] font-mono font-black uppercase text-muted-foreground">Also notify:</div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="rounded-none border border-foreground font-mono text-[9px] font-black uppercase h-8">
                    + Telegram
                  </Button>
                  <Button variant="outline" className="rounded-none border border-foreground font-mono text-[9px] font-black uppercase h-8">
                    + Discord
                  </Button>
                  <Button variant="outline" className="rounded-none border border-foreground font-mono text-[9px] font-black uppercase h-8">
                    + Slack
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-dashed border-foreground/30">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1 rounded-none border-2 border-foreground font-black uppercase text-xs h-11"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase text-xs h-11 shadow-xs"
                >
                  Create My Watch
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Simple toast notification helper
function toast(message: string) {
  const el = document.createElement("div");
  el.className = "fixed bottom-5 right-5 z-50 bg-black text-white dark:bg-white dark:text-black border-2 border-white dark:border-black p-4 font-mono text-[10px] uppercase font-black tracking-wider shadow-md animate-bounce";
  el.innerText = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}
